<template>
  <a-modal
    :title="title"
    :width="1200"
    :visible="visible"
    :confirmLoading="confirmLoading"
    @ok="handleSubmit"
    @cancel="handleCancel"
  >
    <a-spin :spinning="confirmLoading">
      <a-form-model ref="form" :model="form" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
        <a-form-model-item label="选择商品">
          <a-select
            v-model="selectedGoodsIds"
            mode="multiple"
            placeholder="请选择要货商品（支持搜索）"
            show-search
            :filter-option="false"
            :default-active-first-option="false"
            :not-found-content="notFoundContent"
            @search="handleGoodsSearch"
            @change="handleGoodsChange"
            @dropdownVisibleChange="handleDropdownVisibleChange"
            style="width: 100%"
          >
            <template v-if="goodsSearchLoading" slot="notFoundContent">
              <a-spin size="small" />
            </template>
            <template v-else slot="notFoundContent">
              <span>暂无数据</span>
            </template>
            <a-select-option v-for="item in goodsOptions" :key="item.id" :value="item.id">
              {{ item.goodsName }} <span v-if="item.goodsCode" style="color: #999">({{ item.goodsCode }})</span>
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="期望到货日期" prop="expectDate">
          <a-date-picker
            v-model="form.expectDate"
            placeholder="请选择期望到货日期"
            style="width: 300px"
          />
        </a-form-model-item>

        <a-form-model-item label="备注" prop="remark">
          <a-textarea
            v-model="form.remark"
            placeholder="请输入备注信息（选填）"
            :rows="2"
            :maxLength="200"
            style="width: 600px"
          />
        </a-form-model-item>
      </a-form-model>

      <!-- 商品要货数量设置区域 -->
      <div>
        <a-divider>商品要货数量设置</a-divider>

        <!-- 统一设置区域 -->
        <a-form layout="inline" style="margin-bottom: 16px; background: #f5f5f5; padding: 12px; border-radius: 4px;">
          <a-form-item label="统一设置">
            <a-input-number
              v-model="uniformSettings.purchaseCount"
              :min="1"
              :precision="0"
              :max="999999"
              placeholder="要货数量"
              style="width: 120px"
            />
            <a-input-number
              v-model="uniformSettings.alarmStock"
              :min="0"
              :precision="0"
              :max="999999"
              placeholder="预警库存"
              style="width: 120px; margin-left: 8px"
            />
            <a-button type="primary" @click="handleUniformSet" style="margin-left: 8px">
              <a-icon type="check" />统一设置
            </a-button>
            <a-button @click="handleResetAll" style="margin-left: 8px">
              <a-icon type="undo" />重置所有
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 商品表格 -->
        <a-table
          :columns="goodsColumns"
          :data-source="selectedGoods"
          :pagination="false"
          size="small"
          rowKey="id"
          :scroll="{ y: 400 }"
        >
          <span slot="goodsName" slot-scope="text, record">
            <img v-if="record.picture" :src="record.picture" style="width: 40px; height: 40px; margin-right: 8px; vertical-align: middle;" />
            {{ record.goodsName }}
            <a-tag v-if="record.skuName" color="orange" style="margin-left: 8px">{{ record.skuName }}</a-tag>
          </span>
          <span slot="shopPrice" slot-scope="text">
            ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
          </span>
          <span slot="salePrice" slot-scope="text">
            ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
          </span>
          <span slot="purchaseCount" slot-scope="text, record">
            <a-input-number
              v-model="record.purchaseCount"
              :min="1"
              :precision="0"
              :max="999999"
              style="width: 120px"
              placeholder="要货数量"
            />
          </span>
          <span slot="alarmStock" slot-scope="text, record">
            <a-input-number
              v-model="record.alarmStock"
              :min="0"
              :precision="0"
              :max="999999"
              style="width: 100px"
              placeholder="预警库存"
            />
          </span>
          <span slot="amount" slot-scope="text, record">
            ¥{{ ((record.shopPrice || 0) * (record.purchaseCount || 0)).toFixed(2) }}
          </span>
          <span slot="action" slot-scope="text, record">
            <a-button type="link" size="small" @click="removeGoods(record)">
              <a-icon type="delete" />移除
            </a-button>
          </span>
        </a-table>

        <!-- 汇总信息 -->
        <div style="margin-top: 16px; text-align: right; background: #f5f5f5; padding: 16px; border-radius: 4px;">
          <a-statistic
            title="申请总金额"
            :value="totalAmount"
            :precision="2"
            prefix="¥"
          />
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { createPurchaseOrder, getPlatformInventoryGoods } from '@/api/retail/purchase'
import { getGoods } from '@/api/goods/goods'
import { debounce } from 'lodash'
import moment from 'moment'

export default {
  name: 'CreateForm',
  data() {
    return {
      visible: false,
      confirmLoading: false,
      title: '创建要货申请',
      selectedGoodsIds: [],
      selectedGoods: [],
      goodsOptions: [],
      goodsSearchLoading: false,
      notFoundContent: '暂无数据',
      form: {
        orgId: undefined,
        orgType: undefined,
        expectDate: undefined,
        remark: ''
      },
      goodsColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          scopedSlots: { customRender: 'goodsName' },
          width: 250,
          fixed: 'left'
        },
        {
          title: '门店拿货价(元)',
          dataIndex: 'shopPrice',
          key: 'shopPrice',
          scopedSlots: { customRender: 'shopPrice' },
          width: 110,
          align: 'center'
        },
        {
          title: '门店销售价(元)',
          dataIndex: 'salePrice',
          key: 'salePrice',
          scopedSlots: { customRender: 'salePrice' },
          width: 110,
          align: 'center'
        },
        {
          title: '要货数量',
          dataIndex: 'purchaseCount',
          key: 'purchaseCount',
          scopedSlots: { customRender: 'purchaseCount' },
          width: 110,
          align: 'center'
        },
        {
          title: '预警库存',
          dataIndex: 'alarmStock',
          key: 'alarmStock',
          scopedSlots: { customRender: 'alarmStock' },
          width: 110,
          align: 'center'
        },
        {
          title: '小计金额(元)',
          dataIndex: 'amount',
          key: 'amount',
          scopedSlots: { customRender: 'amount' },
          width: 110,
          align: 'center'
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80,
          align: 'center',
          fixed: 'right'
        }
      ],
      uniformSettings: {
        purchaseCount: undefined,
        alarmStock: undefined
      }
    }
  },
  computed: {
    totalAmount() {
      return this.selectedGoods.reduce((total, item) => {
        return total + ((item.shopPrice || 0) * (item.purchaseCount || 0))
      }, 0)
    }
  },
  created() {
    this.handleGoodsSearch = debounce(this.searchGoods, 300)
  },
  methods: {
    handleDropdownVisibleChange(visible) {
      // 当下拉框显示时，如果没有数据，加载全部商品
      if (visible && this.goodsOptions.length === 0 && !this.goodsSearchLoading) {
        this.loadAllGoods()
      }
    },
    handleAdd() {
      this.form.orgId = this.$store.getters.orgId
      this.form.orgType = this.$store.getters.orgType
      this.form.expectDate = undefined
      this.form.remark = ''
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.uniformSettings = {
        purchaseCount: undefined
      }
      // 加载初始商品列表
      this.loadAllGoods()
      this.visible = true
    },
    loadAllGoods() {
      this.goodsSearchLoading = true
      getPlatformInventoryGoods({
        pageNo: 1,
        pageSize: 50
      }).then(response => {
        this.goodsOptions = response.data.records || []
        this.goodsSearchLoading = false
      }).catch(() => {
        this.goodsSearchLoading = false
      })
    },
    searchGoods(value) {
      console.log('CreateForm searchGoods:', value)
      if (!value || value.trim() === '') {
        // 搜索框为空时，不重新加载
        return
      }
      this.goodsSearchLoading = true
      getPlatformInventoryGoods({
        pageNo: 1,
        pageSize: 50,
        goodsName: value
      }).then(response => {
        this.goodsOptions = response.data.records || []
        this.goodsSearchLoading = false
      }).catch(() => {
        this.goodsSearchLoading = false
      })
    },
    getGoodsDetail(goodsId) {
      return getGoods({ id: goodsId }).then(response => {
        return response.data
      })
    },
    async handleGoodsChange(value) {
      // 计算新增的商品
      const existingIds = this.selectedGoods.map(item => item.goodsId)
      const newIds = value.filter(id => !existingIds.includes(id))

      if (newIds.length > 0) {
        // 查询新增商品的详细信息，获取完整的SKU列表
        for (const id of newIds) {
          try {
            const goodsDetail = await this.getGoodsDetail(id)
            if (goodsDetail) {
              // 如果商品有SKU，则需要展开SKU
              if (goodsDetail.skuList && goodsDetail.skuList.length > 0) {
                goodsDetail.skuList.forEach(sku => {
                  const exists = this.selectedGoods.find(item => item.id === sku.id)
                  if (!exists) {
                    const merchantPrice = sku.price || 0
                    this.selectedGoods.push({
                      id: sku.id,
                      goodsId: goodsDetail.id,
                      skuId: sku.id,
                      goodsName: goodsDetail.goodsName,
                      skuName: sku.skuName,
                      picture: sku.picture || goodsDetail.picture,
                      shopPrice: sku.shopPrice || merchantPrice,
                      salePrice: sku.salePrice || merchantPrice,
                      purchaseCount: 1,
                      alarmStock: 0
                    })
                  }
                })
              } else {
                // 单规格商品
                const exists = this.selectedGoods.find(item => item.id === goodsDetail.id)
                if (!exists) {
                  const defaultSkuId = goodsDetail.id
                  const merchantPrice = goodsDetail.price || 0
                  this.selectedGoods.push({
                    id: defaultSkuId,
                    goodsId: goodsDetail.id,
                    skuId: defaultSkuId,
                    goodsName: goodsDetail.goodsName,
                    skuName: '',
                    picture: goodsDetail.picture,
                    shopPrice: goodsDetail.shopPrice || merchantPrice,
                    salePrice: goodsDetail.salePrice || merchantPrice,
                    purchaseCount: 1,
                    alarmStock: 0
                  })
                }
              }
            }
          } catch (error) {
            console.error('获取商品详情失败:', error)
          }
        }
      }

      // 计算需要移除的商品
      const remainingGoodsIds = value
      const allGoodsIdsInTable = []

      // 遍历当前表格中的所有商品
      this.selectedGoods.forEach(item => {
        if (item.skuName) {
          allGoodsIdsInTable.push(item.goodsId)
        } else {
          allGoodsIdsInTable.push(item.goodsId)
        }
      })

      // 找出剩余的商品ID
      const filteredGoods = this.selectedGoods.filter(item => {
        if (item.skuName) {
          return remainingGoodsIds.includes(item.goodsId)
        } else {
          return remainingGoodsIds.includes(item.goodsId)
        }
      })

      this.selectedGoods = filteredGoods
    },
    removeGoods(record) {
      // 从多选框中移除
      if (record.skuName) {
        const sameGoodsSkus = this.selectedGoods.filter(item => item.goodsId === record.goodsId)
        if (sameGoodsSkus.length === 1) {
          const index = this.selectedGoodsIds.indexOf(record.goodsId)
          if (index > -1) {
            this.selectedGoodsIds.splice(index, 1)
          }
        }
      } else {
        const index = this.selectedGoodsIds.indexOf(record.goodsId)
        if (index > -1) {
          this.selectedGoodsIds.splice(index, 1)
        }
      }

      // 从已选列表中移除
      const index = this.selectedGoods.findIndex(item => item.id === record.id)
      if (index > -1) {
        this.selectedGoods.splice(index, 1)
      }
    },
    handleSubmit() {
      if (this.selectedGoods.length === 0) {
        this.$message.warning('请选择商品')
        return
      }

      // 验证数据完整性
      const invalidGoods = this.selectedGoods.find(item => {
        return item.purchaseCount === undefined || item.purchaseCount === null || item.purchaseCount <= 0
      })

      if (invalidGoods) {
        this.$message.warning('请完善所有商品的要货数量')
        return
      }

      this.confirmLoading = true

      // 构建要货申请参数
      const items = this.selectedGoods.map(item => ({
        goodsId: item.goodsId,
        skuId: item.skuId,
        applyCount: item.purchaseCount,
        alarmStock: item.alarmStock || 0,
        unitPrice: item.shopPrice || 0
      }))

      const params = {
        items: items,
        expectDate: this.form.expectDate ? moment(this.form.expectDate).format('YYYY-MM-DD') : null,
        remark: this.form.remark,
        storeId: this.form.orgId,
        merchantId: null
      }

      // 保存要货申请
      createPurchaseOrder(params).then(() => {
        this.$message.success('要货申请已提交，等待商家审核')
        this.visible = false
        this.$emit('ok')
      }).catch(error => {
        this.$message.error('要货申请失败：' + (error.message || '未知错误'))
      }).finally(() => {
        this.confirmLoading = false
      })
    },
    handleCancel() {
      this.visible = false
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.form.expectDate = undefined
      this.form.remark = ''
      this.uniformSettings = {
        purchaseCount: undefined,
        alarmStock: undefined
      }
    },
    // 统一设置
    handleUniformSet() {
      if (!this.uniformSettings.purchaseCount || this.uniformSettings.purchaseCount <= 0) {
        this.$message.warning('请先设置要统一的要货数量')
        return
      }

      this.selectedGoods.forEach(item => {
        if (this.uniformSettings.purchaseCount !== undefined && this.uniformSettings.purchaseCount !== null) {
          item.purchaseCount = this.uniformSettings.purchaseCount
        }
        if (this.uniformSettings.alarmStock !== undefined && this.uniformSettings.alarmStock !== null) {
          item.alarmStock = this.uniformSettings.alarmStock
        }
      })

      this.$message.success('统一设置成功')
    },
    // 重置所有
    handleResetAll() {
      this.$confirm({
        title: '确认重置',
        content: '确定要重置所有商品的要货数量为1，预警库存为0吗？',
        onOk: () => {
          this.selectedGoods.forEach(item => {
            item.purchaseCount = 1
            item.alarmStock = 0
          })
          this.uniformSettings = {
            purchaseCount: 1,
            alarmStock: 0
          }
          this.$message.success('重置成功')
        }
      })
    }
  }
}
</script>

<style scoped>
:deep(.ant-table) {
  font-size: 13px;
}
</style>
